import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  Checkbox,
  Select,
  Spinner,
} from '@shopify/polaris';
import { useApi } from '../contexts/ApiContext.tsx';
import { useToast } from '../contexts/ToastContext.tsx';

interface AppSettings {
  default_delay_days: number;
  auto_sync_enabled: boolean;
  sync_frequency: string;
  notification_email: string;
  widget_enabled: boolean;
  widget_position: string;
  widget_text: string;
}

export const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>({
    default_delay_days: 0,
    auto_sync_enabled: false,
    sync_frequency: 'daily',
    notification_email: '',
    widget_enabled: true,
    widget_position: 'bottom',
    widget_text: 'Delivery may be delayed for this brand',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const { getSettings, updateSettings, syncBrands } = useApi();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSettings();
      setSettings(data.settings || settings);
    } catch (error) {
      showError('Failed to load settings');
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSettings(settings);
      showSuccess('Settings saved successfully');
    } catch (error) {
      showError('Failed to save settings');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      await syncBrands();
      showSuccess('Brands synced successfully');
    } catch (error) {
      showError('Failed to sync brands');
      console.error('Error syncing brands:', error);
    } finally {
      setSyncing(false);
    }
  };

  const syncFrequencyOptions = [
    { label: 'Hourly', value: 'hourly' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Manual', value: 'manual' },
  ];

  const widgetPositionOptions = [
    { label: 'Top of product page', value: 'top' },
    { label: 'Bottom of product page', value: 'bottom' },
    { label: 'Near add to cart button', value: 'cart' },
  ];

  if (loading) {
    return (
      <Page title="Settings">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Settings"
      primaryAction={{
        content: 'Save Settings',
        onAction: handleSave,
        loading: saving,
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            <Card>
              <BlockStack gap="400">
                <Banner tone="info">
                  <p>Configure global settings for the Brand Delivery Delays app.</p>
                </Banner>

                <FormLayout>
                  <TextField
                    label="Default Delay Days"
                    type="number"
                    value={settings.default_delay_days.toString()}
                    onChange={(value) => setSettings({ ...settings, default_delay_days: parseInt(value) || 0 })}
                    helpText="Default number of days to delay delivery for new brands"
                    autoComplete="off"
                  />

                  <TextField
                    label="Notification Email"
                    type="email"
                    value={settings.notification_email}
                    onChange={(value) => setSettings({ ...settings, notification_email: value })}
                    helpText="Email address to receive notifications about sync status"
                    autoComplete="off"
                  />
                </FormLayout>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <h3>Sync Settings</h3>

                <FormLayout>
                  <Checkbox
                    label="Enable Auto Sync"
                    checked={settings.auto_sync_enabled}
                    onChange={(checked) => setSettings({ ...settings, auto_sync_enabled: checked })}
                    helpText="Automatically sync brands from Shopify at regular intervals"
                  />

                  <Select
                    label="Sync Frequency"
                    options={syncFrequencyOptions}
                    value={settings.sync_frequency}
                    onChange={(value) => setSettings({ ...settings, sync_frequency: value })}
                    disabled={!settings.auto_sync_enabled}
                  />

                  <Button onClick={handleSyncNow} loading={syncing}>
                    Sync Now
                  </Button>
                </FormLayout>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <h3>Widget Settings</h3>

                <FormLayout>
                  <Checkbox
                    label="Enable Delivery Widget"
                    checked={settings.widget_enabled}
                    onChange={(checked) => setSettings({ ...settings, widget_enabled: checked })}
                    helpText="Show delivery delay information on product pages"
                  />

                  <Select
                    label="Widget Position"
                    options={widgetPositionOptions}
                    value={settings.widget_position}
                    onChange={(value) => setSettings({ ...settings, widget_position: value })}
                    disabled={!settings.widget_enabled}
                  />

                  <TextField
                    label="Widget Text"
                    value={settings.widget_text}
                    onChange={(value) => setSettings({ ...settings, widget_text: value })}
                    helpText="Text to display when a product has delivery delays"
                    disabled={!settings.widget_enabled}
                    multiline={2}
                    autoComplete="off"
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Settings;
